import { smoothScrollToElement } from './jquery';

/**
 * Handle mobile skip to form button functionality
 */
export const mobileSkipToFormButton = () => {
  const skipToFormButton = document.querySelector('a[href="#form"]') as HTMLAnchorElement | null;
  if (!skipToFormButton) return;

  const firstFormBlock = document.querySelector('.en__component--formblock');
  const firstColumnContainingFormBlock = firstFormBlock?.closest('.en__component--column');

  if (!firstFormBlock || !firstColumnContainingFormBlock) {
    console.error('Could not find column containing form block for mobile skip to form button');
    skipToFormButton.style.display = 'none';

    return;
  }

  // Detect if this column is an only child column -- if so, we scroll to form block, otherwise if there are two columns we scroll to the column containing the form block
  const isOnlyChildColumn = firstColumnContainingFormBlock?.parentElement?.children.length === 1;
  const scrollTargetElement = isOnlyChildColumn ? firstFormBlock : firstColumnContainingFormBlock;

  // Add ID so that link can work as normal if needed
  scrollTargetElement.id = 'form';

  skipToFormButton.addEventListener('click', (event) => {
    event.preventDefault();
    smoothScrollToElement(scrollTargetElement);
  });
};

/**
 * Set certain elements in forms to be shown side by side.
 */
export const sideBySideElements = () => {
  const SIDE_BY_SIDE_CONTAINER_CLASS = 'ia-side-by-side-container';
  const SIDE_BY_SIDE_CLASS = 'ia-side-by-side';
  const SIDE_BY_SIDE_MAX_ELEMENTS = 2; // the maximum number of elements that can be set side by side - if more than this, they will stack as normal
  const SIDE_BY_SIDE_MAX_LABEL_LENGTH = 10; // the maximum number of characters in the label for an element to be considered for side by side - if label is longer than this, element will not be set side by side as it may not fit

  const FIRST_NAME_SELECTOR = '.en__field--firstName';
  const LAST_NAME_SELECTOR = '.en__field--lastName';

  // Stack first name and last name fields side by side if they are both present and next to each other
  const firstNameField = document.querySelector(FIRST_NAME_SELECTOR);
  const lastNameField = document.querySelector(LAST_NAME_SELECTOR);

  if (firstNameField && lastNameField) {
    const firstNameFieldNextSibling = firstNameField.nextElementSibling;

    if (firstNameFieldNextSibling === lastNameField) {
      firstNameField.classList.add(SIDE_BY_SIDE_CLASS);
      lastNameField.classList.add(SIDE_BY_SIDE_CLASS);
      firstNameField.parentElement?.classList.add(SIDE_BY_SIDE_CONTAINER_CLASS);
    }
  }

  // Look for checkboxes and radios to stack side by side

  const checkboxesFields = document.querySelectorAll('.en__field--checkbox');

  checkboxesFields.forEach((field) => {
    const elements = field.querySelectorAll('.en__field__element--checkbox');

    if (elements.length === 1) {
      field.classList.add(SIDE_BY_SIDE_CLASS);
      field.parentElement?.classList.add(SIDE_BY_SIDE_CONTAINER_CLASS);
    }
  });

  const checkboxesRadiosElements = document.querySelectorAll(
    '.en__field__element--checkbox, .en__field__element--radio',
  );

  checkboxesRadiosElements.forEach((element) => {
    let sideBySide = true;

    const fieldItems = element.querySelectorAll('.en__field__item');
    if (fieldItems.length > SIDE_BY_SIDE_MAX_ELEMENTS) sideBySide = false;

    fieldItems.forEach((fieldItem) => {
      const label = fieldItem.querySelector('.en__field__label--item');

      if (label?.textContent?.length && label.textContent.length > SIDE_BY_SIDE_MAX_LABEL_LENGTH)
        sideBySide = false;
    });

    if (sideBySide) {
      element.classList.add(SIDE_BY_SIDE_CLASS);
      element.parentElement?.classList.add(SIDE_BY_SIDE_CONTAINER_CLASS);
    }
  });
};

/**
 * Styles passed into VGS card fields shown inside iframes.
 *
 * https://knowledge.engagingnetworks.net/youraccount/enhancing-security-at-engaging-networks#EnhancingSecurityatEngagingNetworks-HowtomatchyourVGSfieldstylewithyourtextfields
 */
export const vgsCardFieldStyles = () => {
  const borderColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-black')
    .trim();
  const border = `2px solid ${borderColour}`;
  const boxSizing = 'border-box';
  const errorColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-error')
    .trim();
  const violet = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-violet')
    .trim();
  const focusBorderWidth = '4px';
  const focusBoxShadow = 'none';
  const focusOutline = `3px solid ${violet}`;
  const focusOutlineOffset = '0';
  const fontSize = '19px';
  const height = '52.25px';
  const padding = '14.25px';
  const placeholderColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-grey')
    .trim();
  const textColour = getComputedStyle(document.documentElement)
    .getPropertyValue('--colour-text')
    .trim();

  const css = {
    border,
    'box-sizing': boxSizing,
    color: textColour,
    'font-size': fontSize,
    height,
    padding,
    '&:focus-visible': {
      'border-color': borderColour,
      'box-shadow': focusBoxShadow,
      outline: focusOutline,
      'border-width': focusBorderWidth,
      'outline-offset': focusOutlineOffset,
    },
    '&::placeholder': {
      color: placeholderColour,
    },
    '&.invalid.touched': {
      color: errorColour,
    },
    '&.valid': {
      color: textColour,
    },
    '@font-face': {
      'font-family': 'neulis-neue',
      'font-style': 'normal',
      'font-weight': '400',
      src: "url(TODO) format('woff2')",
    },
    'font-family': '"neulis-neue", arial, sans-serif',
  };

  window.enVGSFields = {
    'transaction.ccnumber': {
      css,
    },
    'transaction.ccvv': {
      css,
    },
  };
};
